import { Link } from 'react-router-dom';
import { ArrowRight, Compass, ShieldCheck, CheckCircle, Users, BarChart, Lightbulb, Layers } from 'lucide-react';
import RfPageFrame from '../layout/RfPageFrame';

export default function ServicesAiConsulting() {
  return (
    <>
      <div className="rf-hero-band">
        <RfPageFrame className="!py-10 md:!py-14">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5c6d7e] mb-2">Services · AI Consulting</p>
          <h1 className="text-2xl md:text-[2.25rem] font-semibold text-[#2c2c2c] tracking-tight max-w-3xl leading-tight">
            Advisory for decisions that outlast a single project
          </h1>
          <p className="mt-6 text-[1.05rem] text-[#5c5c5c] max-w-2xl leading-relaxed">
            Workshops, executive briefings, and structured assessments that translate technical options into choices your
            board and regulators can understand—without overselling novelty.
          </p>
        </RfPageFrame>
      </div>

      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          {/* Overview Section */}
          <div className="space-y-6">
            <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c]">Service Overview</h2>
            <p className="text-[#5c5c5c] leading-relaxed max-w-4xl">
              RakFort's AI Consulting provides the strategic foundation for your AI journey. We bridge the gap between 
              complex technical possibilities and business realities, ensuring your AI initiatives are secure, 
              compliant, and aligned with long-term corporate goals. Our advisory services focus on building 
              sustainable frameworks rather than just one-off implementations.
            </p>
          </div>
        </RfPageFrame>
      </section>

      <section className="bg-[#f0f7ff] border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          {/* Categories / Pillars */}
          <div className="space-y-8">
            <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c]">Where we add value</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="rf-card p-8 space-y-4 bg-white">
                <div className="w-12 h-12 bg-[#005a9c]/10 rounded-lg flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6 text-[#005a9c]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c2c2c]">Fit-for-purpose governance</h3>
                <p className="text-[#5c5c5c] text-[0.95rem] leading-relaxed">
                  Development of policies, exceptions, and evidence expectations tailored to your specific regulatory 
                  landscape and risk appetite.
                </p>
              </div>

              <div className="rf-card p-8 space-y-4 bg-white">
                <div className="w-12 h-12 bg-[#005a9c]/10 rounded-lg flex items-center justify-center">
                  <Layers className="w-6 h-6 text-[#005a9c]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c2c2c]">Vendor & Model Selection</h3>
                <p className="text-[#5c5c5c] text-[0.95rem] leading-relaxed">
                  Establishing rigorous selection criteria tied to your sector requirements, data residency needs, 
                  and performance benchmarks.
                </p>
              </div>

              <div className="rf-card p-8 space-y-4 bg-white">
                <div className="w-12 h-12 bg-[#005a9c]/10 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-[#005a9c]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c2c2c]">Risk Treatment Alignment</h3>
                <p className="text-[#5c5c5c] text-[0.95rem] leading-relaxed">
                  Seamlessly connecting security testing outputs (ScanFort) with ongoing risk management (RiskFort) 
                  for a holistic view.
                </p>
              </div>
            </div>
          </div>
        </RfPageFrame>
      </section>

      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          {/* Why Us - Pillars */}
          <div className="space-y-8">
            <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c]">Why Choose RakFort?</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-3">
                <Compass className="w-8 h-8 text-[#005a9c] opacity-80" />
                <h4 className="font-semibold text-[#2c2c2c]">Strategic Clarity</h4>
                <p className="text-sm text-[#5c5c5c] leading-relaxed">
                  We strip away the hype to provide clear, actionable advice that focuses on real business value and 
                  long-term viability.
                </p>
              </div>
              <div className="space-y-3">
                <Users className="w-8 h-8 text-[#005a9c] opacity-80" />
                <h4 className="font-semibold text-[#2c2c2c]">Executive Alignment</h4>
                <p className="text-sm text-[#5c5c5c] leading-relaxed">
                  Our briefings translate technical risk into the language of the board, ensuring buy-in at the 
                  highest levels.
                </p>
              </div>
              <div className="space-y-3">
                <BarChart className="w-8 h-8 text-[#005a9c] opacity-80" />
                <h4 className="font-semibold text-[#2c2c2c]">Evidence-Based</h4>
                <p className="text-sm text-[#5c5c5c] leading-relaxed">
                  Every recommendation is backed by data and aligned with emerging global standards for AI 
                  safety and governance.
                </p>
              </div>
            </div>
          </div>
        </RfPageFrame>
      </section>

      <section className="bg-[#f0f7ff] border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          {/* Our Approach */}
          <div className="rf-card p-8 md:p-10 bg-white border-dashed border-2">
            <h2 className="text-xl font-semibold text-[#2c2c2c] mb-6">Our Engagement Approach</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="font-bold text-[#005a9c] text-lg opacity-40 shrink-0">01</div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c]">Discovery & Workshops</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1">We start by understanding your business goals, existing tech stack, and regulatory environment through collaborative sessions.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="font-bold text-[#005a9c] text-lg opacity-40 shrink-0">02</div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c]">Assessment & Gap Analysis</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1">Comparing your current state against best practices and target objectives to identify critical focus areas.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="font-bold text-[#005a9c] text-lg opacity-40 shrink-0">03</div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c]">Strategy Development</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1">Crafting a bespoke AI governance and implementation strategy that balances innovation with safety.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="font-bold text-[#005a9c] text-lg opacity-40 shrink-0">04</div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c]">Execution Roadmap</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1">Providing a detailed, phased plan for rolling out changes, including milestones and success metrics.</p>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-12 text-[0.9375rem] text-[#5c5c5c]">
            <Link to="/contactus" className="text-[#005a9c] font-medium hover:text-[#004a82] inline-flex items-center gap-1">
              Book a conversation <ArrowRight className="w-4 h-4" />
            </Link>
          </p>
        </RfPageFrame>
      </section>
    </>
  );
}
